import { NgClass, AsyncPipe, KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import {
	FormControl,
	FormGroup,
	FormRecord,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule
} from '@angular/forms';
import { NgxErrorsModule } from '@ngspot/ngx-errors';
import { TranslateModule } from '@ngx-translate/core';
import { BaseFormAccessor, DataFormAccessor } from '@studiohyperdrive/ngx-forms';
import { HasOwnProperty } from '@studiohyperdrive/ngx-utils';
import { isEmpty, isObject } from 'lodash';

import { CypressTagDirective } from '@cjm/cypress/core';
import { ButtonClasses, InfoComponent } from '@cjm/shared/ui/common';
import { CheckBoxComponent } from '@cjm/shared/ui/forms';

import { I18nKeys } from '../../../i18n';
import { FAVORITE_PRODUCTS_FACET_ID } from '../../const';

import { FilterBarForm, FilterBarFormGroup, FilterGroup, FiltersCount } from './filter-bar.types';

@Component({
	selector: 'vloket-filter-bar',
	templateUrl: './filter-bar.component.html',
	styleUrls: ['./filter-bar.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FilterBarComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => FilterBarComponent),
			multi: true
		},
		{
			provide: BaseFormAccessor,
			useExisting: forwardRef(() => FilterBarComponent)
		}
	],
	host: {
		class: 'c-filter-bar'
	},
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		NgxErrorsModule,
		CypressTagDirective,
		NgClass,
		CheckBoxComponent,
		AsyncPipe,
		TranslateModule,
		HasOwnProperty,
		KeyValuePipe,
		InfoComponent
	]
})
export class FilterBarComponent extends DataFormAccessor<
	Record<string, FilterGroup>,
	FilterBarForm,
	FilterBarFormGroup
> {
	@Input() public associationName: string;
	@Input() set filterCount(count: FiltersCount) {
		// Denis: if the given count is not a valid object or is empty, set the count to an empty object.
		if (!isObject(count) || isEmpty(count)) {
			this.count = {};

			return;
		}

		// Denis: if not, set the count property to the given count.
		this.count = count;

		// Denis: run the disableFacets method with the new count values.
		this.disableFacets(this.form, this.count);
	}

	public filters: Record<string, FilterGroup> = {};
	public count: FiltersCount = {};
	public readonly i18nKeys: typeof I18nKeys = I18nKeys;
	public readonly buttonClasses: typeof ButtonClasses = ButtonClasses;
	public readonly FAVORITE_PRODUCTS_FACET_ID: string = FAVORITE_PRODUCTS_FACET_ID;

	// Denis: See: libs/shared/ui/forms/src/lib/abstracts/readme.md
	public initForm(data: Record<string, FilterGroup>): FilterBarFormGroup {
		// Denis: store the given data in the filters property.
		this.filters = data;

		// Denis: set up the new FormGroup.
		const form = this.setUpFormGroup(data);

		// Denis: run the disableFacets method with the new count values.
		this.disableFacets(form, this.count);

		// Denis: return the formGroup to set it to this.form.
		return form;
	}

	/**
	 * setUpFormGroup
	 *
	 * The setUpFormGroup method is used to create a FormGroup from the data that is passed in.
	 *
	 * @param data{Record<string, FilterGroup>} - The data that is used to create the FormGroup.
	 * @returns {FilterBarFormGroup} - The FormGroup that is created from the data.
	 */
	public setUpFormGroup(data: Record<string, FilterGroup>): FilterBarFormGroup {
		const group = Object.entries(data).reduce(
			(acc, [key, filterGroup]) => {
				const record = Object.entries(filterGroup.options).reduce(
					(rec, [optionKey]) => {
						rec[optionKey] = new FormControl(false);
						return rec;
					},
					{} as { [key: string]: FormControl<boolean> }
				);

				acc[key] = new FormRecord(record);
				return acc;
			},
			{} as { [key: string]: FormRecord<FormControl<boolean>> }
		);

		return new FormGroup(group);
	}

	/**
	 * disableFacets
	 *
	 * The disableFacets method is used to disable the facets that have a count of 0.
	 *
	 * @param form{FilterBarFormGroup} - The form that contains the facets.
	 * @param count{FiltersCount} - The count of the facets.
	 */
	public disableFacets(form: FilterBarFormGroup, count: FiltersCount): void {
		if (!form || !count) {
			return;
		}

		Object.entries(count).forEach(([facetKey, facetItems]: [string, Record<string, number>]) => {
			Object.entries(facetItems).forEach(([itemKey, itemValue]: [string, number]) => {
				itemValue === 0
					? form.get(`${facetKey}.${itemKey}`)?.disable({ onlySelf: true, emitEvent: false })
					: form.get(`${facetKey}.${itemKey}`)?.enable({ onlySelf: true, emitEvent: false });
			});
		});
	}
}
